import {
  CONTRACTOR_BADGE_GRADING_DECISION,
  type IncludedFinalGrade,
  type IncludedShift,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";

import {
  BadgeVerificationWindowDays,
  useGetWorkerBadgeVerifications,
} from "../api/useGetWorkerBadgeVerifications";

interface UseShouldShowBadgeVerificationResultParams {
  workerId: string;
  enabled?: boolean;
}

type VerificationsResponse = ReturnType<typeof useGetWorkerBadgeVerifications>["data"];

interface BadgeVerificationResultData {
  verification: NonNullable<VerificationsResponse>["data"][0];
  finalGrade: IncludedFinalGrade;
  shiftId: string;
  qualificationName: string | undefined;
}

interface UseShouldShowBadgeVerificationResultReturn {
  shouldShow: boolean;
  isLoading: boolean;
  data: BadgeVerificationResultData | undefined;
}

export function useShouldShowBadgeVerificationResult(
  params: UseShouldShowBadgeVerificationResultParams
): UseShouldShowBadgeVerificationResultReturn {
  const { workerId, enabled = true } = params;

  const { data: response, isLoading } = useGetWorkerBadgeVerifications(
    workerId,
    BadgeVerificationWindowDays.MISSED_BADGES,
    { includeMissedBadges: true, enabled }
  );

  const included = response?.included ?? [];

  // Find the first graded verification that hasn't been seen by the worker
  const unseenResult = response?.data.find((verification) => {
    if (isDefined(verification.attributes.resultSeenByWorkerAt)) {
      return false;
    }

    const gradeId = verification.relationships.finalGrade?.data?.id;
    if (!isDefined(gradeId)) {
      return false;
    }

    const grade = included.find(
      (resource): resource is IncludedFinalGrade =>
        resource.type === "contractor-badge-verification-grade" && resource.id === gradeId
    );

    return (
      isDefined(grade) &&
      grade.attributes.decision === CONTRACTOR_BADGE_GRADING_DECISION.INCORRECT_BADGE_SUBMISSION
    );
  });

  const finalGrade = isDefined(unseenResult)
    ? included.find(
        (resource): resource is IncludedFinalGrade =>
          resource.type === "contractor-badge-verification-grade" &&
          resource.id === unseenResult.relationships.finalGrade?.data?.id
      )
    : undefined;

  const shiftId = unseenResult?.relationships.shift.data.id;
  const includedShift = included.find(
    (resource): resource is IncludedShift => resource.type === "shift" && resource.id === shiftId
  );
  const isCurrentlyClockedIn =
    isDefined(includedShift?.attributes.clockedInAt) &&
    !isDefined(includedShift?.attributes.clockedOutAt);

  const shouldShow = isDefined(unseenResult) && isDefined(finalGrade) && !isCurrentlyClockedIn;

  if (!shouldShow) {
    return { shouldShow, isLoading, data: undefined };
  }

  return {
    shouldShow,
    isLoading,
    data: {
      verification: unseenResult,
      finalGrade,
      shiftId: unseenResult.relationships.shift.data.id,
      qualificationName: includedShift?.attributes.qualification,
    },
  };
}
